import {
  Authentication,
  WebAuth,
  Auth0Error,
  Auth0UserProfile as UserInfo,
  DefaultLoginOptions,
} from "auth0-js";
import {
  StorageInterface,
  StorageKeys,
} from "../../utils/storage/StorageInterface";
import { UserManager, UserManagerSettings } from "oidc-client-ts";
import { AuthSession, AuthSessionState } from "./AuthSession";
import type { HostSession } from "./host-session";

type Callback<A> = (error: Auth0Error | null, args: A) => void;
export enum ConnectionType {
  GOOGLE = "google-oauth2",
  APPLE = "apple",
  ENTRA = "entra",
  PASSWORD = "Username-Password-Authentication",
}

interface RequestOptions {
  scope: string;
  audience: string;
  redirectUri: string;
}

export interface OAuthOptions {
  domain: string;
  clientId: string;
  scope: string;
  audience: string;
  redirectUri: string;
}

export interface AuthData {
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
  connection?: ConnectionType;
}

/** The pair the backend needs in order to invalidate a session (see `getLogoutCredentials`). */
export interface LogoutCredentials {
  accessToken: string | null;
  refreshToken: string | null;
}

interface TokenResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

const ACCESS_TOKEN_REFRESH_LEEWAY_MS = 60 * 1000;

const TERMINAL_REFRESH_ERROR_CODES = new Set([
  "invalid_grant",
  "invalid_token",
]);

const REJECTED_REFRESH_DESCRIPTIONS = [
  /refresh token (?:was )?(?:rejected|revoked|expired|invalid)/i,
  /invalid refresh token/i,
];

const isTerminalRefreshError = (error: unknown) => {
  const authError = error as Auth0Error & {
    error?: string;
    json?: {
      error?: string;
      error_description?: string;
      description?: string;
    };
    statusCode?: number;
  };
  const code = authError.code || authError.error || authError.json?.error;
  const statusCode = authError.statusCode;
  const description = [
    authError.error_description,
    authError.description,
    authError.json?.error_description,
    authError.json?.description,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    TERMINAL_REFRESH_ERROR_CODES.has(code || "") ||
    ((statusCode === 401 || statusCode === 403) &&
      REJECTED_REFRESH_DESCRIPTIONS.some((pattern) =>
        pattern.test(description),
      ))
  );
};

export class OAuth {
  private client: Authentication;
  private webClient: WebAuth;
  private akClient: UserManager;
  private readonly passwordResetUrl: string;
  private readonly clientId: string;
  private authData!: AuthData;
  private isLoaded = false;
  private readonly requestOptions: RequestOptions;
  private authUser?: UserInfo;
  private refreshTokenPromise?: Promise<void>;
  private sessionExpired = false;

  constructor(
    private storage: StorageInterface,
    { scope, audience, clientId, domain, redirectUri }: OAuthOptions,
    akOptions: UserManagerSettings,
    private hostSession?: HostSession,
  ) {
    this.client = new Authentication({
      scope,
      clientID: clientId,
      domain,
      audience,
      redirectUri,
    });
    this.webClient = new WebAuth({
      scope,
      clientID: clientId,
      domain,
      audience,
    });
    this.akClient = new UserManager(akOptions);
    this.requestOptions = { scope, audience, redirectUri };
    const protocol = domain.startsWith("http") ? "" : "https://";
    this.passwordResetUrl = `${protocol}${domain.replace(
      /\/$/,
      "",
    )}/dbconnections/change_password`;
    this.clientId = clientId;
    this.resetAuthData();
  }

  async getAccessToken(): Promise<string | null> {
    const session = await this.getSession();
    return session.state === AuthSessionState.AUTHENTICATED
      ? session.accessToken
      : null;
  }

  async getSession(): Promise<AuthSession> {
    await this.init();
    if (!this.authData.accessToken) {
      return {
        state: this.sessionExpired
          ? AuthSessionState.EXPIRED
          : AuthSessionState.ANONYMOUS,
      };
    }
    try {
      if (this.shouldRefreshAccessToken()) {
        await this.refreshTokenOnce();
      }
      if (!this.authData.accessToken) {
        return {
          state: this.sessionExpired
            ? AuthSessionState.EXPIRED
            : AuthSessionState.ANONYMOUS,
        };
      }
      return {
        state: AuthSessionState.AUTHENTICATED,
        accessToken: this.authData.accessToken,
      };
    } catch (_e) {
      return {
        state: this.sessionExpired
          ? AuthSessionState.EXPIRED
          : AuthSessionState.REFRESH_UNAVAILABLE,
      };
    }
  }

  async getConnection(): Promise<ConnectionType> {
    await this.init();
    return this.authData.connection!;
  }

  async getSessionIssuer(): Promise<string | null> {
    await this.init();
    return this.getAccessTokenPayload(this.authData.accessToken)?.iss || null;
  }

  /**
   * The credentials the logout mutation has to invalidate, read as one atomic pair.
   *
   * `getAccessToken` is awaited first on purpose: an expired access token is refreshed
   * there, and a provider that rotates refresh tokens hands out a new one in the process
   * (see `setAuthData`). Reading the refresh token before that - or letting the mutation
   * trigger the refresh from inside the auth link - would send the backend the token that
   * the rotation has just replaced: the request would go out with the new bearer, the
   * server would blacklist it, and the refresh token that is actually still valid would
   * survive the logout. That is the exact case an inactivity logout hits, because the
   * access token has usually expired while the user was away.
   *
   * Nothing can rotate the tokens between the await and the read: a refresh only starts
   * from `getSession`, which has just settled.
   *
   * `accessToken` is part of the result rather than an internal detail: a `null` there
   * means the refresh did not go through, so the pair is not usable and the caller must
   * not send it - the auth link would then refresh from inside the request and rotate the
   * refresh token away from the variables it already carries.
   */
  async getLogoutCredentials(): Promise<LogoutCredentials> {
    const accessToken = await this.getAccessToken();
    return {
      accessToken,
      refreshToken: this.authData.refreshToken || null,
    };
  }

  async clearRefreshToken() {
    this.authData.refreshToken = "";
    await this.storage.remove(StorageKeys.REFRESH_TOKEN);
  }

  async isAuthenticated() {
    return !!(await this.getAccessToken());
  }

  hasExpiredSession() {
    return this.sessionExpired;
  }

  async rejectSession() {
    await this.init();
    if (!this.sessionExpired) {
      await this.expireSession();
    }
  }

  async passwordLogin(username: string, password: string) {
    const res = await this.transformAuthError(
      this.promisify<TokenResponse, DefaultLoginOptions>(
        this.client.loginWithDefaultDirectory,
        this.client,
      )({
        username,
        password,
        realm: ConnectionType.PASSWORD,
      }),
    );
    await this.setAuthData(res, ConnectionType.PASSWORD);
  }

  async triggerSocialSignIn(connection: ConnectionType, queryString?: string) {
    await this.storage.set(StorageKeys.CONNECTION, connection);
    this.authData.connection = connection;
    const redirectUri = this.makeSocialRedirectUri(connection, queryString);
    await this.storage.set(StorageKeys.SOCIAL_REDIRECT_URI, redirectUri);

    this.webClient.authorize({
      connection,
      language: this.getLanguage(),
      responseType: "code",
      audience: this.requestOptions.audience,
      scope: this.requestOptions.scope,
      redirectUri,
    });
  }

  async socialSignIn(code: string, connection: string) {
    await this.init();
    const redirectUri =
      (await this.storage.get(StorageKeys.SOCIAL_REDIRECT_URI)) ||
      this.makeSocialRedirectUri(
        (connection || this.authData.connection) as ConnectionType,
      );
    const res = await this.transformAuthError(
      this.promisify(
        this.client.oauthToken,
        this.client,
      )({
        grantType: "authorization_code",
        code,
        ...this.requestOptions,
        redirectUri,
      }),
    );
    if (!res) {
      throw new Error("Invalid data received");
    }
    await this.setAuthData(res as any, this.authData.connection);
    await this.storage.remove(StorageKeys.SOCIAL_REDIRECT_URI);
  }

  async passwordSignUp(
    username: string,
    password: string,
    language = this.getLanguage(),
  ) {
    await this.transformAuthError(
      this.promisify(
        this.client.dbConnection.signup,
        this.client,
      )({
        email: username,
        username,
        password,
        language,
        connection: ConnectionType.PASSWORD,
      } as any),
    );
    return this.passwordLogin(username, password);
  }

  async resetPassword(email: string, language = this.getLanguage()) {
    return this.transformAuthError(
      fetch(this.passwordResetUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: this.clientId,
          connection: ConnectionType.PASSWORD,
          email,
          language,
        }),
      }).then(async (response) => {
        if (!response.ok) {
          const fallbackMessage = `Password reset request failed (${response.status})`;
          let details: Record<string, unknown> = {};
          try {
            details = await response.json();
          } catch (_error) {
            // Some upstream failures return an empty or non-JSON body.
          }
          throw Object.assign(new Error(fallbackMessage), details);
        }
        return undefined;
      }),
    );
  }

  async getAuthUser() {
    if (this.authUser) {
      return this.authUser;
    }
    if (!(await this.isAuthenticated())) {
      return null;
    }
    const token = (await this.getAccessToken()) ?? "";
    this.authUser = await this.promisify(
      this.client.userInfo,
      this.client,
    )(token);
    return this.authUser;
  }

  async logout() {
    this.sessionExpired = false;
    this.resetAuthData();
    await this.clearStorageKeys();
  }

  private async clearStorageKeys() {
    await this.storage.remove(StorageKeys.ACCESS_TOKEN);
    await this.storage.remove(StorageKeys.REFRESH_TOKEN);
    await this.storage.remove(StorageKeys.EXPIRES_AT);
    await this.storage.remove(StorageKeys.CONNECTION);
    await this.storage.remove(StorageKeys.SOCIAL_REDIRECT_URI);
  }

  private makeSocialRedirectUri(
    connection?: ConnectionType,
    queryString?: string,
  ) {
    const url = new URL(
      `${this.requestOptions.redirectUri}${queryString || ""}`,
    );
    if (connection === ConnectionType.ENTRA) {
      url.searchParams.set("connection", ConnectionType.ENTRA);
    }
    return url.toString();
  }

  private async init() {
    if (!this.isLoaded) {
      const accessToken = await this.storage.get(StorageKeys.ACCESS_TOKEN);
      const refreshToken = await this.storage.get(StorageKeys.REFRESH_TOKEN);
      const expiresAtString = await this.storage.get(StorageKeys.EXPIRES_AT);
      const connection = (await this.storage.get(
        StorageKeys.CONNECTION,
        ConnectionType.PASSWORD,
      )) as ConnectionType;
      this.authData.accessToken = accessToken;
      this.authData.refreshToken = refreshToken;
      this.authData.connection = connection;
      const tokenExpiresAt = this.getAccessTokenExpiresAt(accessToken);
      this.authData.expiresAt =
        tokenExpiresAt ||
        // All supported access tokens are JWTs. A present token without a
        // readable expiry is corrupt and must be refreshed instead of being
        // paired with a stale, independently stored expiry.
        (accessToken ? new Date(0) : this.parseExpiresAt(expiresAtString)) ||
        this.authData.expiresAt;
      this.isLoaded = true;
    }
  }

  private resetAuthData() {
    this.authData = {
      accessToken: "",
      refreshToken: "",
      expiresAt: new Date(),
    };
  }

  private async refreshToken() {
    const refreshToken = this.authData.refreshToken;
    if (!refreshToken) {
      if (await this.refreshFromHost()) {
        return;
      }
      await this.expireSession();
      throw new Error("Missing refresh token");
    }
    let res: TokenResponse;
    try {
      res = await this.promisify<TokenResponse, any>(
        this.client.oauthToken,
        this.client,
      )({
        grantType: "refresh_token",
        refresh_token: refreshToken,
        ...this.requestOptions,
      });
    } catch (e) {
      if (
        isTerminalRefreshError(e) &&
        this.authData.refreshToken === refreshToken
      ) {
        await this.expireSession();
      }
      throw e;
    }
    if (this.authData.refreshToken !== refreshToken) {
      return;
    }
    await this.setAuthData(res);
  }

  /**
   * A session borrowed from the mobile app has no refresh token: the app refreshes its own
   * session and writes the new access token into storage. A token that would still need a
   * refresh counts as a failure, so the page cannot ask the app again in a loop.
   */
  private async refreshFromHost(): Promise<boolean> {
    if (!this.hostSession || !(await this.hostSession.refresh())) {
      return false;
    }
    const accessToken = await this.storage.get(StorageKeys.ACCESS_TOKEN);
    const expiresAt = this.getAccessTokenExpiresAt(accessToken);
    if (!expiresAt) {
      return false;
    }
    this.sessionExpired = false;
    this.authData.accessToken = accessToken;
    this.authData.expiresAt = expiresAt;
    return !this.shouldRefreshAccessToken();
  }

  private async expireSession() {
    if (this.sessionExpired) {
      return;
    }
    const { connection } = this.authData;
    this.sessionExpired = true;
    this.authUser = undefined;
    this.resetAuthData();
    this.authData.connection = connection;
    await this.clearStorageKeys();
  }

  private refreshTokenOnce() {
    if (!this.refreshTokenPromise) {
      this.refreshTokenPromise = this.refreshToken().finally(() => {
        this.refreshTokenPromise = undefined;
      });
    }
    return this.refreshTokenPromise;
  }

  private shouldRefreshAccessToken() {
    return (
      Date.now() >=
      this.authData.expiresAt.getTime() - ACCESS_TOKEN_REFRESH_LEEWAY_MS
    );
  }

  private async setAuthData(res: TokenResponse, connection?: ConnectionType) {
    this.sessionExpired = false;
    this.authData = {
      accessToken: res.accessToken,
      refreshToken: res.refreshToken || this.authData.refreshToken,
      expiresAt: this.makeExpiresAt(res),
      connection: connection ?? this.authData.connection,
    };

    await this.storage.set(StorageKeys.ACCESS_TOKEN, this.authData.accessToken);
    await this.storage.set(
      StorageKeys.REFRESH_TOKEN,
      this.authData.refreshToken,
    );
    await this.storage.set(
      StorageKeys.EXPIRES_AT,
      `${this.authData.expiresAt.getTime()}`,
    );
    await this.storage.set(
      StorageKeys.CONNECTION,
      this.authData.connection || "",
    );
  }

  private promisify<A, T>(
    fn: (args: T, cb: Callback<A>) => void,
    context: any,
  ): (args: T) => Promise<A> {
    return (args: T) =>
      new Promise((resolve, reject) => {
        fn.bind(context)(args, (err, callbackArgs) => {
          if (err) {
            reject(err);
          }
          resolve(callbackArgs);
        });
      });
  }

  private makeExpiresAt(res: TokenResponse) {
    return (
      this.getAccessTokenExpiresAt(res.accessToken) ||
      new Date(Date.now() + res.expiresIn * 1000)
    );
  }

  private parseExpiresAt(value: string) {
    const time = parseInt(value, 10);
    return Number.isFinite(time) ? new Date(time) : null;
  }

  private getAccessTokenExpiresAt(accessToken: string) {
    const exp = this.getAccessTokenPayload(accessToken)?.exp;
    return typeof exp === "number" ? new Date(exp * 1000) : null;
  }

  private getAccessTokenPayload(accessToken: string) {
    try {
      const payload = accessToken.split(".")[1];
      if (!payload) {
        return null;
      }
      const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
      const paddingLength = (4 - (normalizedPayload.length % 4)) % 4;
      const decodedPayload = atob(
        `${normalizedPayload}${"=".repeat(paddingLength)}`,
      );
      return JSON.parse(decodedPayload);
    } catch (_e) {
      return null;
    }
  }

  private transformAuthError<T>(promise: Promise<T>): Promise<T> {
    return promise.catch((e) => {
      let { message } = e;
      const code = e.code || e?.error || e?.json?.error;
      const friendlyMessage =
        e.friendly_message ||
        e.error_description ||
        e.description ||
        e?.json?.friendly_message ||
        e?.json?.error_description ||
        e?.json?.description;
      if (code === "terna_requires_entra" && friendlyMessage) {
        message = friendlyMessage;
      } else if (code) {
        message = `auth.errors.${code}.translation`;
      }
      throw new Error(message?.toString());
    });
  }

  private getLanguage() {
    return navigator.language;
  }
}
